import React, { useState, useRef, useLayoutEffect } from 'react';
import { Popper, Fade, Paper, TextField, MenuItem, Box, Typography } from '@mui/material';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { enqueueSnackbarAction } from '../../store/snackbar/actions';
import RangePicker from './components/range-picker';
import Clock from '../../assets/icons/clock-icon';
import makeStyles from './styles';
import { parametersAsObject, uniqueId, urlUpdate } from '../../globals/utils';
import { QueryParameterKeys } from '../../globals/paramter';

const historyFromPresent: { value: string[] | string; label: string }[] = [
  {
    value: ['167', 'hours'],
    label: 'Last 7 days',
  },
  {
    value: ['24', 'hours'],
    label: 'Last 24 Hours',
  },
  {
    value: ['6', 'hours'],
    label: 'Last 6 Hours',
  },
  {
    value: 'range',
    label: 'Specific Time Range',
  },
];

const TimeRange = ({ blockFutureDates }: { blockFutureDates?: boolean }) => {
  const [formValue, setFormValue] = useState(historyFromPresent[0].value);
  const [anchorElement, setAnchorElement] = useState<null | any>(null);
  const [options, setOptions] = useState(historyFromPresent);
  const popLocation = useRef(null);
  const history = useHistory();
  const classes = makeStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParameters = location.search;
  const parameters = new URLSearchParams(queryParameters);
  const startTime = parameters.get(QueryParameterKeys.startTime);
  const endTime = parameters.get(QueryParameterKeys.endTime);
  const timeFrame = parameters.get(QueryParameterKeys.timeFrame);

  const changeCustomRangeValue = (startRange: string, endRange: string) => {
    const optionsMutable = [...options];
    optionsMutable[optionsMutable.length - 1] = {
      ...optionsMutable[optionsMutable.length - 1],
      label: `${moment.utc(startRange).format('MM/DD/YYYY HH:mm')} - ${moment
        .utc(endRange)
        .format('MM/DD/YYYY HH:mm')}`,
    };
    setOptions(optionsMutable);
  };

  const utilizeQueryParameters = () => {
    if (!timeFrame) {
      urlUpdate(history, parametersAsObject(parameters), {
        startTime: `${moment().subtract(`167`, `hours`).startOf("minute").toISOString()}`,
        endTime: `${moment().endOf("minute").toISOString()}`,
        timeFrame: '167',
      });
    } else {
      if (startTime && endTime && timeFrame === 'custom') {
        setFormValue('range');
        changeCustomRangeValue(startTime, endTime);
      }
      if (timeFrame !== 'custom' && timeFrame !== formValue) {
        setFormValue(timeFrame);
        urlUpdate(history, parametersAsObject(parameters), {
          startTime: `${moment().subtract(`${timeFrame}`, `hours`).startOf("minute").toISOString()}`,
          endTime: `${moment().endOf("minute").toISOString()}`,
          timeFrame,
        });
      }
    }
  };
  useLayoutEffect(() => {
    utilizeQueryParameters();
  }, [timeFrame, startTime, endTime]);

  const handleChange = (event: any) => {
    const { value } = event.target;
    if (value !== 'range')
      urlUpdate(history, parametersAsObject(parameters), {
        startTime: `${moment().subtract(`${value}`, `hours`).startOf("minute").toISOString()}`,
        endTime: `${moment().endOf("minute").toISOString()}`,
        timeFrame: value,
      });
    setAnchorElement(value === 'range' ? value : null);
  };

  const handleClick = (value: string | string[]) => {
    if (value === historyFromPresent[3].value) setAnchorElement(true);
    setFormValue(value);
  };
  const open = Boolean(anchorElement);
  const id = open ? 'simple-popper' : undefined;
  const rangeSelectionHandler = (timeRange: string[] | null) => {

    if (timeRange) {
      changeCustomRangeValue(timeRange[0], timeRange[1]);
      urlUpdate(history, parametersAsObject(parameters), {
        startTime: `${timeRange[0]}`,
        endTime: `${timeRange[1]}`,
        timeFrame: 'custom',
      });
      setAnchorElement(false);
    } else {
      dispatch(
        enqueueSnackbarAction({
          message: 'Future date not allowed!',
          options: { variant: 'error' },
        }),
      );
    }
  };

  return (
    <Box display="flex" alignItems="center">
      <Typography>Time Frame:</Typography>
      <Box pl={1} pt={0.5} pr={0.5}>
        <Clock className={classes.clockIcon} />
      </Box>
      <TextField
        variant="standard"
        id={id}
        select
        value={formValue}
        onChange={handleChange}
        ref={popLocation}
      >
        {options.map((option) => (
          <MenuItem
            key={uniqueId()}
            value={option.value !== 'range' ? option.value[0] : option.value}
          >
            <div
              role="button"
              tabIndex={0}
              onKeyPress={() => handleClick(option.value)}
              onClick={() => handleClick(option.value)}
            >
              {option.label}
            </div>
          </MenuItem>
        ))}
      </TextField>
      <Popper
        open={open}
        anchorEl={popLocation.current}
        placement="bottom-end"
        transition
        style={{ zIndex: 99 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={300}>
            <Paper>
              <RangePicker
                blockFutureDates={blockFutureDates}
                close={setAnchorElement}
                rangeSelectionHandler={rangeSelectionHandler}
                inputTimeRange={options[3].label}
              />
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default TimeRange;
